import { useEffect, useState } from "react";
import { Smartphone, Download, Zap, WifiOff, Share, X } from "lucide-react";
import { AnimateIn } from "@/hooks/useAnimations";

/**
 * PWA install section. Pitches the site as an installable web app.
 *  - Chrome/Edge/Android: captures `beforeinstallprompt` and triggers native prompt
 *  - iOS Safari: shows the "Share → Add to Home Screen" hint
 *  - Already-installed standalone users: section auto-hides
 */
function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function isIOS() {
  if (typeof window === "undefined") return false;
  return /iPhone|iPad|iPod/.test(window.navigator.userAgent) && !window.MSStream;
}

export default function AppDownloadSection() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [ios, setIos] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    setIos(isIOS());
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    const installedHandler = () => setInstalled(true);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  // Already installed → hide the section entirely to avoid redundancy.
  if (installed) return null;

  const handleInstall = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") setInstalled(true);
        setDeferred(null);
      } catch (_) { /* noop */ }
      return;
    }
    // No deferred prompt available yet (browser hasn't fired beforeinstallprompt
    // in this session, or user dismissed it). Show step-by-step instructions
    // instead of silently failing.
    setShowHelp(true);
  };

  return (
    <section id="app" className="bg-[#0A0A0A] py-16 sm:py-24 relative overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 dot-pattern opacity-40 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

          {/* Left: text */}
          <div>
            <AnimateIn delay={0.18}>
              <p className="text-xs sm:text-sm font-bold tracking-[0.3em] uppercase text-red-600 mb-4 section-label">
                INSTALLABLE WEB APP
              </p>
            </AnimateIn>
            <AnimateIn delay={0.36}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-[0.95] mb-5">
                KoKo Sites<br />
                <span className="text-red-600">On Your Home Screen</span>
              </h2>
            </AnimateIn>
            <AnimateIn delay={0.54}>
              <p className="text-zinc-400 leading-relaxed mb-8 max-w-xl">
                One tap to install. No Play Store, no App Store, no 50&nbsp;MB download &mdash; just our full site
                as a standalone app on your phone or desktop. Works offline, launches instantly, lives right
                next to your other apps.
              </p>
            </AnimateIn>

            <AnimateIn delay={0.72}>
              <div className="grid grid-cols-3 gap-3 mb-8 max-w-md">
                <Perk icon={Zap} text="INSTANT" />
                <Perk icon={WifiOff} text="OFFLINE" />
                <Perk icon={Smartphone} text="STANDALONE" />
              </div>
            </AnimateIn>

            <AnimateIn delay={0.9}>
              <div className="flex flex-col sm:flex-row items-start gap-3">
                {ios ? (
                  <div
                    data-testid="pwa-ios-hint"
                    className="border-2 border-white/20 bg-[#111] text-zinc-300 text-xs sm:text-sm px-5 py-4 inline-flex items-center gap-3 max-w-md"
                  >
                    <Share size={18} className="text-red-500 shrink-0" />
                    <span>
                      Tap the <strong className="text-white">Share</strong> icon in Safari, then choose{" "}
                      <strong className="text-white">&ldquo;Add to Home Screen&rdquo;</strong>.
                    </span>
                  </div>
                ) : (
                  <button
                    onClick={handleInstall}
                    data-testid="pwa-install-btn"
                    className="btn-brutal btn-shine group bg-red-600 text-white font-bold uppercase tracking-widest py-4 px-8 border-2 border-white hover:bg-white hover:text-black text-xs sm:text-sm flex items-center gap-3"
                  >
                    <Download size={16} className="group-hover:translate-y-0.5 transition-transform" />
                    {deferred ? "Install Web App" : "Install From Browser Menu"}
                  </button>
                )}
                <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 pt-2">
                  Android &middot; iOS &middot; Desktop
                </span>
              </div>
            </AnimateIn>

            <AnimateIn delay={1.08}>
              <p className="mt-6 text-[11px] text-zinc-600 uppercase tracking-widest max-w-md">
                {deferred
                  ? "One tap installs. Uninstall anytime like a regular app."
                  : "If the button is disabled, use your browser's menu \u2192 Install app."}
              </p>
            </AnimateIn>
          </div>

          {/* Right: phone mockup */}
          <AnimateIn delay={0.54} direction="right">
            <div className="relative flex justify-center">
              <div className="relative w-[260px] sm:w-[300px] aspect-[9/19]">
                {/* Phone frame */}
                <div className="absolute inset-0 bg-[#111] border-[3px] border-white/20 rounded-[40px] shadow-[10px_10px_0_0_rgba(229,9,20,0.9)]">
                  {/* notch */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-b-xl z-10" />
                  {/* screen */}
                  <div className="absolute inset-2 bg-[#0A0A0A] rounded-[32px] overflow-hidden flex flex-col">
                    {/* fake status bar */}
                    <div className="h-8" />
                    {/* fake hero */}
                    <div className="px-4 pt-6">
                      <p className="text-[8px] font-bold tracking-[0.2em] text-red-600 mb-2">
                        &#9632; WEB DESIGN STUDIO
                      </p>
                      <p className="text-white font-black text-xl leading-none tracking-tighter uppercase">
                        We Build
                      </p>
                      <p className="text-red-600 font-black text-xl leading-none tracking-tighter uppercase glitch-hover">
                        Websites
                      </p>
                      <p className="text-white font-black text-xl leading-none tracking-tighter uppercase mb-3">
                        That Convert
                      </p>
                      <p className="text-[9px] text-zinc-500 leading-tight mb-4">
                        Stunning sites starting at Rs 15,000
                      </p>
                      <div className="bg-red-600 border border-white py-2 px-3 shadow-[3px_3px_0_0_#fff] mb-2">
                        <p className="text-white font-bold text-[9px] text-center tracking-widest">
                          GET YOUR WEBSITE &rarr;
                        </p>
                      </div>
                      <div className="border border-white/40 py-2 px-3 shadow-[3px_3px_0_0_rgba(255,255,255,0.4)]">
                        <p className="text-white font-bold text-[9px] text-center tracking-widest">
                          CONTACT US
                        </p>
                      </div>
                    </div>
                    {/* tab bar */}
                    <div className="mt-auto border-t border-white/10 flex justify-around py-2 px-2">
                      {["HOME", "SRVC", "WIZ", "FAQ", "MSG"].map((t, i) => (
                        <span key={t} className={`text-[7px] font-bold tracking-wider ${i === 2 ? "text-red-600" : "text-zinc-500"}`}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                {/* floating K badge */}
                <div className="absolute -top-5 -right-5 w-16 h-16 bg-red-600 flex items-center justify-center text-white font-black text-3xl border-2 border-white shadow-[4px_4px_0_0_rgba(0,0,0,0.6)] animate-float">
                  K
                </div>
              </div>
            </div>
          </AnimateIn>
        </div>
      </div>

      {showHelp && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowHelp(false)}
          data-testid="pwa-help-modal"
        >
          <div
            className="relative w-full max-w-md border-2 border-white/15 bg-[#0A0A0A] p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowHelp(false)}
              aria-label="Close"
              className="absolute top-3 right-3 text-zinc-500 hover:text-white p-1"
            >
              <X size={16} />
            </button>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-red-500 mb-3">
              Install From Browser Menu
            </p>
            <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight text-white mb-5 leading-tight">
              Add KoKo Sites to your home screen
            </h3>
            <ol className="space-y-4 text-sm text-zinc-300 leading-relaxed">
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 bg-red-600 text-white font-black text-xs flex items-center justify-center">1</span>
                <span>
                  Open your browser's menu &mdash; usually{" "}
                  <strong className="text-white">⋮</strong> (Chrome/Edge) or{" "}
                  <strong className="text-white">⋯</strong> at the top-right / bottom of the screen.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 bg-red-600 text-white font-black text-xs flex items-center justify-center">2</span>
                <span>
                  Tap <strong className="text-white">&ldquo;Install app&rdquo;</strong>,{" "}
                  <strong className="text-white">&ldquo;Add to Home Screen&rdquo;</strong>, or{" "}
                  <strong className="text-white">&ldquo;Install KoKo Sites&rdquo;</strong>.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 bg-red-600 text-white font-black text-xs flex items-center justify-center">3</span>
                <span>Confirm &mdash; the KoKo Sites icon will appear next to your other apps.</span>
              </li>
            </ol>
            <button
              onClick={() => setShowHelp(false)}
              data-testid="pwa-help-close"
              className="mt-6 w-full btn-brutal btn-shine bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-6 border-2 border-white hover:bg-white hover:text-black text-xs"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Perk({ icon: Icon, text }) {
  return (
    <div className="border border-white/10 bg-[#111] p-3 flex flex-col items-center gap-1 card-accent hover:border-red-600/60 transition-all duration-300 hover:-translate-y-1">
      <Icon size={18} className="text-red-600" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{text}</span>
    </div>
  );
}
